/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react"
import { css, Dialog, DialogContent, DialogActions, Box, Tooltip, IconButton } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import { withMyTheme } from "../../../../theme/theme"
import { mobileCss } from "../../../../util/isMobile"
import { MyButton } from "../../../../components/button/MyButton"
import { MyInput } from "../../../../components/input/MyInput"
import { useTranslation } from "react-i18next"
import { MenuSection, MenuIconType } from "../../../common/context/SharedFormTypes"
import { MenuIconPicker } from "./MenuIconPicker"
import { Switch, FormControlLabel, Typography } from "@mui/material"

const DialogContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 2rem 2rem 1rem;

    ${mobileCss(`
        padding: 1.5rem 1rem 0.5rem;
    `)}
`)

const DialogActionsStyle = withMyTheme(() => css`
    padding: 1rem 2rem;
    justify-content: center;
    gap: 1rem;
`)

const ToggleRowStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;

    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 0.95rem;
        color: ${theme.palette.text.primary};
    }
`)

const SwitchStyle = withMyTheme((theme) => css`
    & .MuiSwitch-switchBase.Mui-checked {
        color: ${theme.palette.primary.main};
    }
    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
    }
`)

const InfoIconStyle = withMyTheme((theme) => css`
    color: ${theme.palette.text.secondary};
    font-size: 1.1rem;
`)

interface MenuSectionDialogProps {
    open: boolean
    section: MenuSection | null
    primaryColor: string
    onSave: (section: MenuSection) => void
    onClose: () => void
}

export const MenuSectionDialog = ({ open, section, primaryColor, onSave, onClose }: MenuSectionDialogProps) => {
    const { t } = useTranslation()
    const [title, setTitle] = useState('')
    const [iconType, setIconType] = useState<MenuIconType>('emoji')
    const [iconValue, setIconValue] = useState('🍽️')
    const [details, setDetails] = useState('')
    const [expandInDialog, setExpandInDialog] = useState(false)

    useEffect(() => {
        if (section) {
            setTitle(section.title)
            setIconType(section.iconType)
            setIconValue(section.iconValue)
            setDetails(section.details)
            setExpandInDialog(section.expandInDialog)
        } else {
            setTitle('')
            setIconType('emoji')
            setIconValue('🍽️')
            setDetails('')
            setExpandInDialog(false)
        }
    }, [section, open])

    const handleIconSelect = (type: MenuIconType, value: string) => {
        setIconType(type)
        setIconValue(value)
    }

    const handleSave = () => {
        onSave({
            id: section?.id ?? `menu-section-${Date.now()}`,
            title,
            iconType,
            iconValue,
            details,
            expandInDialog
        })
        onClose()
    }

    const canSave = title.trim().length > 0

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            <DialogContent css={DialogContentStyle}>
                <MyInput
                    value={title}
                    label={t('questionnaire.menu.sectionDialog.title')}
                    placeholder={t('questionnaire.menu.sectionDialog.titlePlaceholder')}
                    onChange={setTitle}
                />

                <MenuIconPicker
                    iconType={iconType}
                    iconValue={iconValue}
                    primaryColor={primaryColor}
                    onSelect={handleIconSelect}
                />

                <MyInput
                    value={details}
                    label={t('questionnaire.menu.sectionDialog.details')}
                    placeholder={t('questionnaire.menu.sectionDialog.detailsPlaceholder')}
                    onChange={setDetails}
                    multiline
                    rows={4}
                />

                <Box css={ToggleRowStyle}>
                    <FormControlLabel
                        control={
                            <Switch
                                css={SwitchStyle}
                                checked={expandInDialog}
                                onChange={() => setExpandInDialog(!expandInDialog)}
                                size="small"
                            />
                        }
                        label={t('questionnaire.menu.sectionDialog.expandToggle')}
                    />
                    <Tooltip
                        title={t('questionnaire.menu.sectionDialog.expandTooltip') as string}
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                        arrow
                    >
                        <IconButton size="small">
                            <InfoOutlined css={InfoIconStyle} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogContent>

            <DialogActions css={DialogActionsStyle}>
                <MyButton
                    variant="secondary"
                    text={t('questionnaire.menu.sectionDialog.cancel')}
                    onClick={onClose}
                />
                <MyButton
                    variant="primary"
                    text={t('questionnaire.menu.sectionDialog.save')}
                    onClick={handleSave}
                    disabled={!canSave}
                />
            </DialogActions>
        </Dialog>
    )
}
